import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { errMsg } from "@/components/email/EmParts";
import { OwnershipQueue, WooNewCustomers } from "@/components/email/OwnershipQueue";
import { JobLinks } from "@/components/email/JobLinks";
import { ConsentVerification } from "@/components/email/ConsentVerification";
import { WritebackPanels } from "@/components/email/WritebackPanels";
import { OwnershipPriority } from "@/components/email/ReadinessGroups";

const PFX = { "no_marketing_permission": "No marketing permission", opportunity: "Possible extra evidence", source: "Order source", status: "Order status", year: "Order year" };
const label = (k) => {
  const [p, rest] = k.split(":");
  const base = (s) => s.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
  return rest !== undefined ? `${PFX[p] || base(p)} — ${base(rest)}` : base(k);
};

function Audit({ title, desc, path, testId }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); try { setD((await api.get(path)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const block = (name, obj) => (obj && Object.keys(obj).length > 0 ? (
    <div className="mt-3"><p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{name}</p>
      <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3 mt-1">{Object.entries(obj).map(([k, v]) => (
        <div key={k} className="rounded-lg bg-slate-50 px-3 py-1.5 flex justify-between gap-2 text-sm" data-testid={`${testId}-${k}`}><span className="text-slate-600">{label(k)}</span><b>{Array.isArray(v) ? v.join(", ") : String(v)}</b></div>))}</div></div>) : null);
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2"><div className="mr-auto max-w-3xl"><h2 className="text-base font-bold text-slate-900">{title}</h2><p className="text-sm text-slate-500">{desc}</p></div>
        <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid={`${testId}-run`}>{busy ? "Checking…" : d ? "Re-check" : "Run check"}</Button></div>
      {d && <>
        {block("Results", d.counts)}{block("Consent categories (existing consent review)", d.consent_categories)}{block("Purchase sources", d.purchase_sources)}{block("Records that could not be linked to a customer", d.unresolved_records)}
        {block("Unmatched orders", d.unmatched)}{block("Ambiguous orders", d.ambiguous)}{block("Accessories filed under Hot Tubs / Swim Spas", d.miscategorised_by_keyword)}
        {block("Model families", d.families)}{block("Proposed internal structure", d.proposed_structure)}
        {d.inferred_ranges && <p className="text-xs text-slate-500 mt-3" data-testid={`${testId}-ranges`}>Ranges inferred from filter/cover purchases: {d.inferred_ranges.map(([r, n]) => `${r} ${n}`).join(" · ")}</p>}
        {d.rules && <p className="text-xs text-slate-500 mt-3">{d.rules}</p>}
        {d.catalogue_note && <p className="text-xs text-slate-400 mt-1">{d.catalogue_note}</p>}</>}
    </section>
  );
}

export default function EmReadiness() {
  return (
    <div className="space-y-5" data-testid="em-readiness">
      <Link to="/email-marketing/automations" className="text-xs text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" />Automations</Link>
      <p className="text-sm text-slate-600">Read-only checks before any automation is switched on. Nothing is changed or sent.</p>
      <Audit title="Hot tub ownership" testId="rd-own" path="/email-marketing/readiness/ownership"
        desc="Who TedOps can recognise as a hot tub / swim spa owner from jobs, installations, quotes, web and EPOS sales, serial numbers and the equipment register, plus owners inferred from model-specific filter and cover purchases." />
      <Audit title="Consent audiences" testId="rd-consent" path="/email-marketing/readiness/consent"
        desc="Who could receive service messages and who could receive marketing, using the existing email-marketing permission and suppression rules." />
      <ConsentVerification />
      <WritebackPanels />
      <Audit title="WooCommerce matching quality" testId="rd-woo" path="/email-marketing/readiness/woo-quality"
        desc="Why orders are unmatched or ambiguous, and where reliable extra evidence could help. Nothing is linked automatically." />
      <OwnershipPriority />
      <OwnershipQueue />
      <JobLinks />
      <Audit title="Product catalogue structure" testId="rd-cat" path="/email-marketing/readiness/catalogue"
        desc="Missing manufacturers, accessories filed under Hot Tubs / Swim Spas and model families. Internal review only — WooCommerce listings, categories, URLs, stock and prices are untouched." />
      <WooNewCustomers />
    </div>
  );
}
